import { useState, useEffect } from "react";

// https://usehooks.com/useDebounce/
export function useDebounce(value: any, delay: number) {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(
    () => {
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);
      // Cancel the timeout if value changes (also on delay change or unmount)
      // This is how we prevent debounced value from updating if value is
      // changed within the delay period. Timeout gets cleared and restarted.
      return () => {
        clearTimeout(handler);
      };
    },
    [value, delay] // Only re-call effect if value or delay changes
  );
  return debouncedValue;
}

/**
 * Reports whether an element's content overflows its box, tracking changes
 * with a ResizeObserver.
 *
 * Returns a callback ref rather than an object ref so the element is tracked
 * in state -- the observer needs to re-attach when the node changes, which a
 * plain ref cannot signal. The returned ref is referentially stable (it is a
 * useState setter), so attaching it does not detach and re-attach every render.
 */
export function useOverflowDetector<T extends HTMLElement = HTMLElement>() {
  const [overflow, setOverflow] = useState(false);
  const [el, setEl] = useState<T | null>(null);

  useEffect(() => {
    if (!el) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        // there should only ever be one entry (the observed element)
        const target = entry.target;

        setOverflow(
          target.scrollWidth > target.clientWidth ||
            target.scrollHeight > target.clientHeight
        );
      }
    });

    resizeObserver.observe(el);

    return () => {
      resizeObserver.disconnect();
    };
  }, [el]);

  return { ref: setEl, overflow };
}
